import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';

import SoftAvatar from 'components/SoftAvatar';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import {
  showSuiErrorSnackbar,
  showSuiSuccessSnackbar,
} from 'components/SuiSnackbar';
import SoftTypography from 'components/SoftTypography';

import { GraphqlRequestError } from 'apollo/graphql-request';
import { useAuth } from 'auth';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import {
  CategorySelect,
  ExclusionsAutocomplete,
  fetchProfileByUuid,
  getExcludedSkillUuids,
  LanguagesAutocomplete,
  CitiesAutocomplete,
  PROFILE_CONTACT_TYPE_OPTIONS,
  PROFILE_SOCIAL_PLATFORM_OPTIONS,
  PROFILE_EXCLUSION_INVALID_ERROR_CODE,
  PROFILE_EXCLUSION_SKILL_CONFLICT_ERROR_CODE,
  PROFILE_SLUG_TAKEN_ERROR_CODE,
  readCachedProfileByUuid,
  SkillsAutocomplete,
  uploadProfileAvatarRequest,
  updateProfileRequest,
  type ProfileContactInput,
  type ProfileContactType,
  type ProfileCategory,
  type ProfileExclusion,
  type ProfileDetails,
  type ProfileCityInput,
  type ProfileLanguage,
  type ProfileSocialLinkInput,
  type ProfileSocialPlatform,
  type ProfileSkill,
} from 'profile';
import { useLocale } from 'lib/i18n/client';
import { getTranslations, interpolateTemplate } from 'lib/i18n/dictionary';
import { fetchStorageUsage, type StorageUsage } from 'storage/storage-usage.api';
import { formatStorageUsage } from 'storage/storage-usage.format';

function getProfileInitials(profile: ProfileDetails | null): string {
  const label = profile?.title?.trim() || profile?.account?.username?.trim() || '';
  if (!label) {
    return 'PR';
  }

  const lettersOnlyLabel = label.replace(/[^A-Za-z\s._@-]+/g, ' ');
  const parts = lettersOnlyLabel.split(/[\s._@-]+/).filter(Boolean);
  if (parts.length === 0) {
    return 'PR';
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function resolveLocalizedProfileUpdateErrorMessage(
  error: unknown,
  translations: ReturnType<typeof getTranslations>['adminProfileEditor']
): string | null {
  if (!(error instanceof GraphqlRequestError) || !error.code) {
    return null;
  }

  switch (error.code) {
    case PROFILE_SLUG_TAKEN_ERROR_CODE:
      return translations.slugTaken;
    case PROFILE_EXCLUSION_INVALID_ERROR_CODE:
      return translations.exclusionInvalid;
    case PROFILE_EXCLUSION_SKILL_CONFLICT_ERROR_CODE:
      return translations.exclusionSkillConflict;
    default:
      return null;
  }
}

const MAX_PROFILE_UPLOAD_SIZE_BYTES = 50 * 1024 * 1024;

function getLocalizedContactTypeLabel(
  type: ProfileContactType,
  translations: ReturnType<typeof getTranslations>['profileDetailsPage']
): string {
  switch (type) {
    case 'PHONE':
      return translations.contactPhone;
    case 'EMAIL':
      return translations.contactEmail;
    default:
      return translations.contactOther;
  }
}

function getLocalizedSocialPlatformLabel(
  platform: ProfileSocialPlatform,
  translations: ReturnType<typeof getTranslations>['profileDetailsPage']
): string {
  switch (platform) {
    case 'TWITTER_X':
      return translations.socialTwitter;
    case 'INSTAGRAM':
      return translations.socialInstagram;
    case 'THREADS':
      return translations.socialThreads;
    case 'FACEBOOK':
      return translations.socialFacebook;
    case 'TIKTOK':
      return translations.socialTikTok;
    case 'YOUTUBE':
      return translations.socialYouTube;
    case 'WEBSITE':
      return translations.socialWebsite;
    default:
      return translations.socialOther;
  }
}

function ProfileDetailsPage() {
  const { uuid = '' } = useParams();
  const { accessToken } = useAuth();
  const locale = useLocale();
  const translations = getTranslations(locale);
  const profileEditorTranslations = translations.adminProfileEditor;
  const profileDetailsTranslations = translations.profileDetailsPage;
  const cachedProfile = uuid.trim() ? readCachedProfileByUuid(uuid) : undefined;
  const [profile, setProfile] = useState<ProfileDetails | null>(() => cachedProfile ?? null);
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);
  const [isLoading, setIsLoading] = useState(() => cachedProfile === undefined);
  const [errorMessage, setErrorMessage] = useState('');
  const [titleInput, setTitleInput] = useState('');
  const [slugInput, setSlugInput] = useState('');
  const [firstNameInput, setFirstNameInput] = useState('');
  const [lastNameInput, setLastNameInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [coverageRadiusInput, setCoverageRadiusInput] = useState('50');
  const [contactsInput, setContactsInput] = useState<ProfileContactInput[]>([]);
  const [locationsInput, setLocationsInput] = useState<ProfileCityInput[]>([]);
  const [socialLinksInput, setSocialLinksInput] = useState<ProfileSocialLinkInput[]>([]);
  const [categoriesInput, setCategoriesInput] = useState<ProfileCategory[]>([]);
  const [skillsInput, setSkillsInput] = useState<ProfileSkill[]>([]);
  const [exclusionsInput, setExclusionsInput] = useState<ProfileExclusion[]>([]);
  const [languagesInput, setLanguagesInput] = useState<ProfileLanguage[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateErrorMessage, setUpdateErrorMessage] = useState('');
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState('');
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [avatarUploadErrorMessage, setAvatarUploadErrorMessage] = useState('');
  const avatarFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (errorMessage) {
      showSuiErrorSnackbar(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (updateErrorMessage) {
      showSuiErrorSnackbar(updateErrorMessage);
    }
  }, [updateErrorMessage]);

  useEffect(() => {
    if (avatarUploadErrorMessage) {
      showSuiErrorSnackbar(avatarUploadErrorMessage);
    }
  }, [avatarUploadErrorMessage]);

  useEffect(() => {
    if (updateSuccessMessage) {
      showSuiSuccessSnackbar(updateSuccessMessage);
    }
  }, [updateSuccessMessage]);

  const applyProfileDetails = (
    fetchedProfile: NonNullable<ReturnType<typeof readCachedProfileByUuid>>
  ) => {
    setProfile(fetchedProfile);
    setTitleInput(fetchedProfile.title?.trim() || '');
    setSlugInput(fetchedProfile.slug?.trim() || '');
    setFirstNameInput(fetchedProfile.firstName?.trim() || '');
    setLastNameInput(fetchedProfile.lastName?.trim() || '');
    setDescriptionInput(fetchedProfile.description?.trim() || '');
    setCoverageRadiusInput(String(fetchedProfile.coverageRadius ?? 50));
    setContactsInput(
      (fetchedProfile.contacts || []).map((contact) => ({
        type: contact.type || PROFILE_CONTACT_TYPE_OPTIONS[0].value,
        value: contact.value?.trim() || '',
      }))
    );
    setLocationsInput(
      (fetchedProfile.cities || []).map((city) => ({
        placeId: city.placeId?.trim() || '',
        label: city.label?.trim() || '',
      }))
    );
    setSocialLinksInput(
      (fetchedProfile.socialLinks || []).map((socialLink) => ({
        platform: socialLink.platform || PROFILE_SOCIAL_PLATFORM_OPTIONS[0].value,
        url: socialLink.url?.trim() || '',
      }))
    );
    setCategoriesInput(fetchedProfile.categories || []);
    setSkillsInput(fetchedProfile.skills || []);
    setExclusionsInput(fetchedProfile.exclusions || []);
    setLanguagesInput(fetchedProfile.languages || []);
    setUpdateErrorMessage('');
    setUpdateSuccessMessage('');
  };

  useEffect(() => {
    let isCancelled = false;

    async function loadProfile() {
      const cachedData = readCachedProfileByUuid(uuid);
      if (!isCancelled && cachedData !== undefined) {
        if (cachedData) {
          applyProfileDetails(cachedData);
        }
        setIsLoading(false);
      } else if (!isCancelled) {
        setIsLoading(true);
      }

      setErrorMessage('');

      try {
        if (!uuid.trim()) {
          throw new Error(profileEditorTranslations.profileUuidMissing);
        }

        const fetchedProfile = await fetchProfileByUuid(uuid, accessToken ?? undefined);

        if (isCancelled) {
          return;
        }

        if (!fetchedProfile) {
          setProfile(null);
          setCategoriesInput([]);
          setErrorMessage(profileEditorTranslations.profileNotFound);
          return;
        }

        applyProfileDetails(fetchedProfile);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setProfile(null);
        setCategoriesInput([]);
        setErrorMessage(
          error instanceof Error ? error.message : profileEditorTranslations.profileLoadFailed
        );
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadProfile();

    return () => {
      isCancelled = true;
    };
  }, [accessToken, profileEditorTranslations, uuid]);

  const loadStorageUsage = useCallback(async () => {
    try {
      setStorageUsage(await fetchStorageUsage(accessToken ?? undefined));
    } catch {
      setStorageUsage(null);
    }
  }, [accessToken]);

  useEffect(() => {
    void loadStorageUsage();
  }, [loadStorageUsage]);

  const profileInitials = useMemo(() => getProfileInitials(profile), [profile]);
  const hasAvatar = Boolean(profile?.avatarUrl?.trim());
  const localizedContactTypeOptions = useMemo(
    () =>
      PROFILE_CONTACT_TYPE_OPTIONS.map((option) => ({
        ...option,
        label: getLocalizedContactTypeLabel(option.value, profileDetailsTranslations),
      })),
    [profileDetailsTranslations]
  );
  const localizedSocialPlatformOptions = useMemo(
    () =>
      PROFILE_SOCIAL_PLATFORM_OPTIONS.map((option) => ({
        ...option,
        label: getLocalizedSocialPlatformLabel(option.value, profileDetailsTranslations),
      })),
    [profileDetailsTranslations]
  );

  const handleOpenAvatarPicker = () => {
    if (isLoading || isAvatarUploading || !profile) {
      return;
    }

    setAvatarUploadErrorMessage('');
    avatarFileInputRef.current?.click();
  };

  const handleAvatarFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    event.target.value = '';

    if (!selectedFile || !profile) {
      return;
    }

    if (!selectedFile.type.trim().toLowerCase().startsWith('image/')) {
      setAvatarUploadErrorMessage(profileEditorTranslations.avatarFileType);
      return;
    }

    if (selectedFile.size > MAX_PROFILE_UPLOAD_SIZE_BYTES) {
      setAvatarUploadErrorMessage(profileEditorTranslations.avatarUploadSizeLimit);
      return;
    }

    setIsAvatarUploading(true);
    setAvatarUploadErrorMessage('');
    setUpdateErrorMessage('');
    setUpdateSuccessMessage('');

    try {
      const uploadResult = await uploadProfileAvatarRequest(
        {
          profileUuid: profile.uuid,
          file: selectedFile,
        },
        accessToken ?? undefined
      );

      if (uploadResult.profileUuid !== profile.uuid) {
        throw new Error(profileEditorTranslations.avatarMismatch);
      }

      setProfile((currentProfile) => {
        if (!currentProfile || currentProfile.uuid !== uploadResult.profileUuid) {
          return currentProfile;
        }

        return {
          ...currentProfile,
          avatarUrl: uploadResult.avatarUrl ?? null,
        };
      });
      setUpdateSuccessMessage(profileEditorTranslations.avatarUploaded);
      void loadStorageUsage();
    } catch (error) {
      setAvatarUploadErrorMessage(
        error instanceof Error ? error.message : profileEditorTranslations.avatarUploadFailed
      );
    } finally {
      setIsAvatarUploading(false);
    }
  };

  const handleContactChange = (index: number, field: keyof ProfileContactInput, value: string) => {
    setContactsInput((currentContacts) =>
      currentContacts.map((currentContact, currentIndex) =>
        currentIndex === index
          ? {
              ...currentContact,
              [field]: field === 'type' ? (value as ProfileContactType) : value,
            }
          : currentContact
      )
    );
    setUpdateErrorMessage('');
    setUpdateSuccessMessage('');
  };

  const handleAddContact = () => {
    setContactsInput((currentContacts) => [
      ...currentContacts,
      {
        type: PROFILE_CONTACT_TYPE_OPTIONS[0].value,
        value: '',
      },
    ]);
    setUpdateErrorMessage('');
    setUpdateSuccessMessage('');
  };

  const handleRemoveContact = (index: number) => {
    setContactsInput((currentContacts) =>
      currentContacts.filter((_, currentIndex) => currentIndex !== index)
    );
    setUpdateErrorMessage('');
    setUpdateSuccessMessage('');
  };

  const handleSocialLinkChange = (
    index: number,
    field: keyof ProfileSocialLinkInput,
    value: string
  ) => {
    setSocialLinksInput((currentLinks) =>
      currentLinks.map((currentLink, currentIndex) =>
        currentIndex === index
          ? {
              ...currentLink,
              [field]: field === 'platform' ? (value as ProfileSocialPlatform) : value,
            }
          : currentLink
      )
    );
    setUpdateErrorMessage('');
    setUpdateSuccessMessage('');
  };

  const handleAddSocialLink = () => {
    setSocialLinksInput((currentLinks) => [
      ...currentLinks,
      {
        platform: PROFILE_SOCIAL_PLATFORM_OPTIONS[0].value,
        url: '',
      },
    ]);
    setUpdateErrorMessage('');
    setUpdateSuccessMessage('');
  };

  const handleRemoveSocialLink = (index: number) => {
    setSocialLinksInput((currentLinks) =>
      currentLinks.filter((_, currentIndex) => currentIndex !== index)
    );
    setUpdateErrorMessage('');
    setUpdateSuccessMessage('');
  };

  const handleSaveProfile = async () => {
    if (!profile) {
      return;
    }

    const normalizedTitle = titleInput.trim();
    const normalizedSlug = slugInput.trim();
    const normalizedFirstName = firstNameInput.trim();
    const normalizedLastName = lastNameInput.trim();
    const normalizedDescription = descriptionInput.trim();
    const normalizedCoverageRadiusInput = coverageRadiusInput.trim();
    const normalizedContactsDraft: ProfileContactInput[] = contactsInput.map((contact) => ({
      type: contact.type,
      value: contact.value.trim(),
    }));
    const normalizedSocialLinksDraft: ProfileSocialLinkInput[] = socialLinksInput.map(
      (socialLink) => ({
        platform: socialLink.platform,
        url: socialLink.url.trim(),
      })
    );
    const normalizedLocationsDraft: ProfileCityInput[] = locationsInput.map((city) => ({
      placeId: city.placeId.trim(),
      label: city.label.trim(),
    }));
    const normalizedCoverageRadius = Number(normalizedCoverageRadiusInput);

    if (normalizedContactsDraft.some((contact) => contact.value.length === 0)) {
      setUpdateSuccessMessage('');
      setUpdateErrorMessage(profileEditorTranslations.contactMissingValue);
      return;
    }
    const normalizedContacts = normalizedContactsDraft.filter(
      (contact) => contact.value.length > 0
    );
    if (normalizedSocialLinksDraft.some((socialLink) => socialLink.url.length === 0)) {
      setUpdateSuccessMessage('');
      setUpdateErrorMessage(profileEditorTranslations.socialMissingUrl);
      return;
    }

    const normalizedSocialLinks = normalizedSocialLinksDraft.filter(
      (socialLink) => socialLink.url.length > 0
    );
    const uniquePlatforms = new Set(normalizedSocialLinks.map((socialLink) => socialLink.platform));
    if (uniquePlatforms.size !== normalizedSocialLinks.length) {
      setUpdateSuccessMessage('');
      setUpdateErrorMessage(profileEditorTranslations.socialUniquePlatform);
      return;
    }

    if (normalizedLocationsDraft.some((city) => city.label.length === 0)) {
      setUpdateSuccessMessage('');
      setUpdateErrorMessage(profileEditorTranslations.cityMissingValue);
      return;
    }

    if (normalizedLocationsDraft.some((city) => city.placeId.length === 0)) {
      setUpdateSuccessMessage('');
      setUpdateErrorMessage(profileEditorTranslations.cityPlaceIdMissing);
      return;
    }

    const normalizedLocations = normalizedLocationsDraft.filter(
      (city) => city.label.length > 0 && city.placeId.length > 0
    );
    const uniqueLocationPlaceIds = new Set(normalizedLocations.map((city) => city.placeId));
    if (uniqueLocationPlaceIds.size !== normalizedLocations.length) {
      setUpdateSuccessMessage('');
      setUpdateErrorMessage(profileEditorTranslations.cityDuplicate);
      return;
    }

    if (normalizedTitle.length < 2) {
      setUpdateSuccessMessage('');
      setUpdateErrorMessage(profileEditorTranslations.titleValidation);
      return;
    }
    if (
      normalizedCoverageRadiusInput.length === 0 ||
      !Number.isFinite(normalizedCoverageRadius) ||
      normalizedCoverageRadius < 0 ||
      normalizedCoverageRadius > 1000 ||
      !Number.isInteger(normalizedCoverageRadius)
    ) {
      setUpdateSuccessMessage('');
      setUpdateErrorMessage(profileEditorTranslations.coverageRadiusValidation);
      return;
    }
    if (normalizedSlug.length > 0 && normalizedSlug.length < 3) {
      setUpdateSuccessMessage('');
      setUpdateErrorMessage(profileEditorTranslations.slugMin);
      return;
    }
    if (normalizedSlug.length > 128) {
      setUpdateSuccessMessage('');
      setUpdateErrorMessage(profileEditorTranslations.slugMax);
      return;
    }

    setIsUpdating(true);
    setUpdateErrorMessage('');
    setUpdateSuccessMessage('');

    try {
      const updatedProfile = await updateProfileRequest(
        {
          uuid: profile.uuid,
          title: normalizedTitle,
          coverageRadius: normalizedCoverageRadius,
          slug: normalizedSlug || undefined,
          firstName: normalizedFirstName || null,
          lastName: normalizedLastName || null,
          description: normalizedDescription || undefined,
          contacts: normalizedContacts,
          cities: normalizedLocations,
          socialLinks: normalizedSocialLinks,
          categories: categoriesInput,
          skills: skillsInput,
          exclusions: exclusionsInput,
          languages: languagesInput,
        },
        accessToken ?? undefined
      );

      setProfile(updatedProfile);
      setTitleInput(updatedProfile.title?.trim() || '');
      setSlugInput(updatedProfile.slug?.trim() || '');
      setFirstNameInput(updatedProfile.firstName?.trim() || '');
      setLastNameInput(updatedProfile.lastName?.trim() || '');
      setDescriptionInput(updatedProfile.description?.trim() || '');
      setCoverageRadiusInput(String(updatedProfile.coverageRadius ?? 50));
      setContactsInput(
        (updatedProfile.contacts || []).map((contact) => ({
          type: contact.type || PROFILE_CONTACT_TYPE_OPTIONS[0].value,
          value: contact.value?.trim() || '',
        }))
      );
      setLocationsInput(
        (updatedProfile.cities || []).map((city) => ({
          placeId: city.placeId?.trim() || '',
          label: city.label?.trim() || '',
        }))
      );
      setSocialLinksInput(
        (updatedProfile.socialLinks || []).map((socialLink) => ({
          platform: socialLink.platform || PROFILE_SOCIAL_PLATFORM_OPTIONS[0].value,
          url: socialLink.url?.trim() || '',
        }))
      );
      setCategoriesInput(updatedProfile.categories || []);
      setSkillsInput(updatedProfile.skills || []);
      setExclusionsInput(updatedProfile.exclusions || []);
      setLanguagesInput(updatedProfile.languages || []);
      setUpdateSuccessMessage(profileEditorTranslations.profileUpdated);
    } catch (error) {
      const localizedUpdateErrorMessage = resolveLocalizedProfileUpdateErrorMessage(
        error,
        profileEditorTranslations
      );
      if (localizedUpdateErrorMessage) {
        setUpdateErrorMessage(localizedUpdateErrorMessage);
      } else {
        setUpdateErrorMessage(
          error instanceof Error ? error.message : profileEditorTranslations.profileUpdateFailed
        );
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const excludedSkillUuids = getExcludedSkillUuids(exclusionsInput);

  const isSaveProfileDisabled =
    isUpdating ||
    isAvatarUploading ||
    isLoading ||
    Boolean(errorMessage) ||
    !profile;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox mt={4} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card id="profile">
              <SoftBox p={2}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item>
                    <SoftAvatar
                      bgColor="info"
                      size="xl"
                      shadow="sm"
                      alt={profileEditorTranslations.profileAvatarAlt}
                      src={profile?.avatarUrl ?? undefined}
                      sx={
                        hasAvatar
                          ? {
                              '& img, & .MuiAvatar-img': {
                                minHeight: 'unset !important',
                              },
                            }
                          : undefined
                      }
                    >
                      {profileInitials}
                    </SoftAvatar>
                  </Grid>
                  <Grid item xs>
                    <SoftBox height="100%" mt={0.5} lineHeight={1}>
                      <SoftTypography variant="h5" fontWeight="medium">
                        {profile?.title?.trim() ||
                          profileEditorTranslations.profileCardFallbackTitle}
                      </SoftTypography>
                      <SoftTypography variant="button" color="text" fontWeight="medium">
                        {profile?.account?.username
                          ? profile.account.username
                          : profileEditorTranslations.profileCardFallbackSubtitle}
                      </SoftTypography>
                      {storageUsage ? (
                        <SoftTypography
                          data-testid="profile-storage-usage"
                          display="block"
                          mt={0.5}
                          variant="caption"
                          color="text"
                        >
                          {formatStorageUsage(
                            storageUsage,
                            profileDetailsTranslations.storageUsageTemplate,
                            profileDetailsTranslations.storageUsageUnlimitedTemplate
                          )}
                        </SoftTypography>
                      ) : null}
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} sm="auto">
                    <input
                      ref={avatarFileInputRef}
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(event) => {
                        void handleAvatarFileChange(event);
                      }}
                    />
                    <SoftButton
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={handleOpenAvatarPicker}
                      disabled={isAvatarUploading || isLoading || !profile}
                    >
                      <Icon sx={{ mr: 0.5 }} fontSize="small">
                        upload
                      </Icon>
                      {isAvatarUploading
                        ? profileEditorTranslations.uploadingAvatar
                        : profileEditorTranslations.uploadAvatar}
                    </SoftButton>
                  </Grid>
                </Grid>
              </SoftBox>
            </Card>
          </Grid>

          {isLoading ? (
            <Grid item xs={12}>
              <Card>
                <SoftBox
                  py={8}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  gap={1.5}
                >
                  <CircularProgress color="info" />
                  <SoftTypography variant="button" color="text">
                    {profileEditorTranslations.loadingProfile}
                  </SoftTypography>
                </SoftBox>
              </Card>
            </Grid>
          ) : !profile ? (
            <Grid item xs={12}>
              <Card>
                <SoftBox p={3}>
                  <SoftTypography variant="button" color="text" fontWeight="regular">
                    {profileEditorTranslations.unavailable}
                  </SoftTypography>
                </SoftBox>
              </Card>
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <Card id="basic-info">
                  <SoftBox p={3}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <SoftBox mb={2}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {profileEditorTranslations.firstNameLabel}
                              </SoftTypography>
                              <SoftBox mt={0.5}>
                                <SoftInput
                                  value={firstNameInput}
                                  placeholder={profileEditorTranslations.firstNamePlaceholder}
                                  onChange={(event) => {
                                    setFirstNameInput(event.target.value);
                                    setUpdateErrorMessage('');
                                    setUpdateSuccessMessage('');
                                  }}
                                />
                              </SoftBox>
                            </SoftBox>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SoftBox mb={2}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {profileEditorTranslations.lastNameLabel}
                              </SoftTypography>
                              <SoftBox mt={0.5}>
                                <SoftInput
                                  value={lastNameInput}
                                  placeholder={profileEditorTranslations.lastNamePlaceholder}
                                  onChange={(event) => {
                                    setLastNameInput(event.target.value);
                                    setUpdateErrorMessage('');
                                    setUpdateSuccessMessage('');
                                  }}
                                />
                              </SoftBox>
                            </SoftBox>
                          </Grid>
                        </Grid>

                        <SoftBox mb={2}>
                          <SoftTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            {profileEditorTranslations.titleLabel}
                          </SoftTypography>
                          <SoftBox mt={0.5}>
                            <SoftInput
                              value={titleInput}
                              placeholder={profileEditorTranslations.titlePlaceholder}
                              onChange={(event) => {
                                setTitleInput(event.target.value);
                                setUpdateErrorMessage('');
                                setUpdateSuccessMessage('');
                              }}
                            />
                          </SoftBox>
                        </SoftBox>

                        <SoftBox mb={2}>
                          <SoftTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            {profileEditorTranslations.descriptionLabel}
                          </SoftTypography>
                          <SoftBox mt={0.5}>
                            <SoftInput
                              multiline
                              rows={4}
                              value={descriptionInput}
                              placeholder={profileEditorTranslations.descriptionPlaceholder}
                              onChange={(event) => {
                                setDescriptionInput(event.target.value);
                                setUpdateErrorMessage('');
                                setUpdateSuccessMessage('');
                              }}
                            />
                          </SoftBox>
                        </SoftBox>

                        <SoftBox mb={2}>
                          <SoftTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            {profileEditorTranslations.categoriesLabel}
                          </SoftTypography>
                          <SoftBox mt={0.5}>
                            <CategorySelect
                              accessToken={accessToken}
                              value={categoriesInput}
                              onChange={(nextCategories: ProfileCategory[]) => {
                                setCategoriesInput(nextCategories);
                                setUpdateErrorMessage('');
                                setUpdateSuccessMessage('');
                              }}
                              disabled={isUpdating}
                            />
                          </SoftBox>
                        </SoftBox>

                        <SoftBox mb={2}>
                          <SoftTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            {profileEditorTranslations.skillsLabel}
                          </SoftTypography>
                          <SoftBox mt={0.5}>
                            <SkillsAutocomplete
                              accessToken={accessToken}
                              selectedCategories={categoriesInput}
                              excludedSkillUuids={excludedSkillUuids}
                              value={skillsInput}
                              onChange={(nextSkills: ProfileSkill[]) => {
                                setSkillsInput(nextSkills);
                                setUpdateErrorMessage('');
                                setUpdateSuccessMessage('');
                              }}
                              disabled={isUpdating}
                            />
                          </SoftBox>
                        </SoftBox>

                        <SoftBox mb={2}>
                          <SoftTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            {profileEditorTranslations.exclusionsLabel}
                          </SoftTypography>
                          <SoftBox mt={0.5}>
                            <ExclusionsAutocomplete
                              accessToken={accessToken}
                              profileSkills={skillsInput}
                              value={exclusionsInput}
                              onChange={(nextExclusions: ProfileExclusion[]) => {
                                setExclusionsInput(nextExclusions);
                                setUpdateErrorMessage('');
                                setUpdateSuccessMessage('');
                              }}
                              disabled={isUpdating}
                            />
                          </SoftBox>
                        </SoftBox>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                          <SoftTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            {profileEditorTranslations.slugLabel}
                            <Tooltip
                              title={interpolateTemplate(profileEditorTranslations.slugTooltip, {
                                slug:
                                  slugInput.trim() ||
                                  profile.slug ||
                                  profileEditorTranslations.slugPlaceholder,
                              })}
                              placement="top"
                              arrow
                            >
                              <Icon
                                fontSize="small"
                                sx={{
                                  color: 'text.secondary',
                                  cursor: 'help',
                                  fontSize: '1rem',
                                  ml: 0.5,
                                  verticalAlign: 'text-bottom',
                                }}
                              >
                                info
                              </Icon>
                            </Tooltip>
                          </SoftTypography>
                          <SoftBox mt={0.5}>
                            <SoftInput
                              value={slugInput}
                              placeholder={profileEditorTranslations.slugPlaceholder}
                              onChange={(event) => {
                                setSlugInput(event.target.value);
                                setUpdateErrorMessage('');
                                setUpdateSuccessMessage('');
                              }}
                            />
                          </SoftBox>
                        </SoftBox>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={8}>
                            <SoftBox mb={2}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {profileEditorTranslations.citiesLabel}
                              </SoftTypography>
                              <SoftBox mt={0.5}>
                                <CitiesAutocomplete
                                  accessToken={accessToken}
                                  value={locationsInput}
                                  onChange={(nextLocations) => {
                                    setLocationsInput(nextLocations);
                                    setUpdateErrorMessage('');
                                    setUpdateSuccessMessage('');
                                  }}
                                  disabled={isUpdating}
                                />
                              </SoftBox>
                            </SoftBox>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <SoftBox mb={2}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {profileEditorTranslations.coverageRadiusLabel}
                              </SoftTypography>
                              <SoftBox mt={0.5}>
                                <SoftInput
                                  type="number"
                                  value={coverageRadiusInput}
                                  placeholder={profileEditorTranslations.coverageRadiusPlaceholder}
                                  inputProps={{ min: 0, max: 1000, step: 1 }}
                                  onChange={(event) => {
                                    setCoverageRadiusInput(event.target.value);
                                    setUpdateErrorMessage('');
                                    setUpdateSuccessMessage('');
                                  }}
                                />
                              </SoftBox>
                            </SoftBox>
                          </Grid>
                        </Grid>

                        <SoftBox mb={2}>
                          <SoftTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            {profileEditorTranslations.languagesLabel}
                          </SoftTypography>
                          <SoftBox mt={0.5}>
                            <LanguagesAutocomplete
                              accessToken={accessToken}
                              value={languagesInput}
                              onChange={(nextLanguages: ProfileLanguage[]) => {
                                setLanguagesInput(nextLanguages);
                                setUpdateErrorMessage('');
                                setUpdateSuccessMessage('');
                              }}
                              disabled={isUpdating}
                            />
                          </SoftBox>
                        </SoftBox>

                        <SoftBox mb={2}>
                          <SoftBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <SoftTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              {profileEditorTranslations.contactsLabel}
                            </SoftTypography>
                            <SoftButton
                              variant="outlined"
                              color="info"
                              size="small"
                              onClick={handleAddContact}
                            >
                              <Icon sx={{ mr: 0.5 }} fontSize="small">
                                add
                              </Icon>
                              {profileEditorTranslations.addContact}
                            </SoftButton>
                          </SoftBox>
                          {contactsInput.length === 0 ? (
                            <SoftTypography variant="caption" color="text">
                              {profileEditorTranslations.noContacts}
                            </SoftTypography>
                          ) : null}
                          {contactsInput.map((contact, index) => (
                            <Grid container spacing={1.5} key={`contact-row-${index}`}>
                              <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                  <Select
                                    input={<SoftInput />}
                                    value={contact.type}
                                    onChange={(event) => {
                                      handleContactChange(index, 'type', event.target.value);
                                    }}
                                  >
                                    {localizedContactTypeOptions.map((contactTypeOption) => (
                                      <MenuItem
                                        key={`${contactTypeOption.value}-${index}`}
                                        value={contactTypeOption.value}
                                      >
                                        {contactTypeOption.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={7}>
                                <SoftInput
                                  type={
                                    contact.type === 'EMAIL'
                                      ? 'email'
                                      : contact.type === 'PHONE'
                                        ? 'tel'
                                        : 'text'
                                  }
                                  value={contact.value}
                                  placeholder={
                                    contact.type === 'EMAIL'
                                      ? profileEditorTranslations.contactEmailPlaceholder
                                      : contact.type === 'PHONE'
                                        ? profileEditorTranslations.contactPhonePlaceholder
                                        : profileEditorTranslations.contactDetailsPlaceholder
                                  }
                                  onChange={(event) => {
                                    handleContactChange(index, 'value', event.target.value);
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} md={1}>
                                <SoftButton
                                  variant="text"
                                  color="error"
                                  size="small"
                                  onClick={() => {
                                    handleRemoveContact(index);
                                  }}
                                >
                                  <Icon fontSize="small">delete</Icon>
                                </SoftButton>
                              </Grid>
                            </Grid>
                          ))}
                        </SoftBox>

                        <SoftBox mb={2}>
                          <SoftBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <SoftTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              {profileEditorTranslations.socialLinksLabel}
                            </SoftTypography>
                            <SoftButton
                              variant="outlined"
                              color="info"
                              size="small"
                              onClick={handleAddSocialLink}
                            >
                              <Icon sx={{ mr: 0.5 }} fontSize="small">
                                add
                              </Icon>
                              {profileEditorTranslations.addLink}
                            </SoftButton>
                          </SoftBox>
                          {socialLinksInput.length === 0 ? (
                            <SoftTypography variant="caption" color="text">
                              {profileEditorTranslations.noLinks}
                            </SoftTypography>
                          ) : null}
                          {socialLinksInput.map((socialLink, index) => (
                            <Grid container spacing={1.5} key={`social-link-row-${index}`}>
                              <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                  <Select
                                    input={<SoftInput />}
                                    value={socialLink.platform}
                                    onChange={(event) => {
                                      handleSocialLinkChange(index, 'platform', event.target.value);
                                    }}
                                  >
                                    {localizedSocialPlatformOptions.map((socialPlatformOption) => (
                                      <MenuItem
                                        key={`${socialPlatformOption.value}-${index}`}
                                        value={socialPlatformOption.value}
                                      >
                                        {socialPlatformOption.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={7}>
                                <SoftInput
                                  type="url"
                                  value={socialLink.url}
                                  placeholder={profileEditorTranslations.socialLinkPlaceholder}
                                  onChange={(event) => {
                                    handleSocialLinkChange(index, 'url', event.target.value);
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} md={1}>
                                <SoftButton
                                  variant="text"
                                  color="error"
                                  size="small"
                                  onClick={() => {
                                    handleRemoveSocialLink(index);
                                  }}
                                >
                                  <Icon fontSize="small">delete</Icon>
                                </SoftButton>
                              </Grid>
                            </Grid>
                          ))}
                        </SoftBox>
                      </Grid>
                    </Grid>

                    <SoftBox mt={3} display="flex" justifyContent="flex-end">
                      <SoftButton
                        variant="gradient"
                        color="info"
                        size="small"
                        onClick={handleSaveProfile}
                        disabled={isSaveProfileDisabled}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        {isUpdating
                          ? profileEditorTranslations.saving
                          : profileEditorTranslations.saveChanges}
                      </SoftButton>
                    </SoftBox>
                  </SoftBox>
                </Card>
              </Grid>
            </>
          )}
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ProfileDetailsPage;
